import { ERROR_CODES, type AuthPayload, type PageMeta } from '@stoplord/shared';
import { useAuth } from '@/store/auth';

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api/v1';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
    public requestId?: string,
  ) {
    super(message);
  }
  /** Field-level validation errors keyed by path. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    if (Array.isArray(this.details)) for (const d of this.details as { path: string; message: string }[]) out[d.path] = d.message;
    return out;
  }
}

type Query = Record<string, string | number | boolean | null | undefined>;

interface Options {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
}

const SIGNED_OUT_CODES = new Set<string>([ERROR_CODES.SESSION_REVOKED, ERROR_CODES.SESSION_IDLE_TIMEOUT, ERROR_CODES.UNAUTHENTICATED]);

function url(path: string, query?: Query) {
  const u = new URL(`${BASE}${path}`, window.location.origin);
  for (const [k, v] of Object.entries(query ?? {})) if (v !== undefined && v !== null && v !== '') u.searchParams.set(k, String(v));
  return u.toString();
}

let refreshing: Promise<AuthPayload | null> | null = null;

/** Exchange the HttpOnly refresh cookie for a new access token. Single-flight per tab. */
export function refreshSession(): Promise<AuthPayload | null> {
  refreshing ??= (async () => {
    try {
      const res = await fetch(url('/auth/refresh'), { method: 'POST', credentials: 'include', headers: { 'x-stoplord-client': 'web' } });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.success) {
        const code = json?.error?.code as string | undefined;
        useAuth.getState().clear(code === ERROR_CODES.SESSION_IDLE_TIMEOUT ? 'idle' : null);
        return null;
      }
      useAuth.getState().setSession(json.data as AuthPayload);
      return json.data as AuthPayload;
    } catch {
      useAuth.getState().clear(null);
      return null;
    } finally {
      setTimeout(() => (refreshing = null), 0);
    }
  })();
  return refreshing;
}

async function request<T>(path: string, opts: Options = {}, retried = false): Promise<{ data: T; meta?: PageMeta }> {
  const token = useAuth.getState().accessToken;
  const res = await fetch(url(path, opts.query), {
    method: opts.method ?? 'GET',
    credentials: 'include',
    signal: opts.signal,
    headers: {
      'x-stoplord-client': 'web',
      ...(opts.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  const json = await res.json().catch(() => null);
  if (res.ok && json?.success) return { data: json.data as T, meta: json.meta };

  const err = json?.error ?? { code: ERROR_CODES.INTERNAL, message: res.statusText || 'Request failed' };
  if (res.status === 401 && err.code === ERROR_CODES.TOKEN_EXPIRED && !retried) {
    if (await refreshSession()) return request<T>(path, opts, true);
  }
  if (res.status === 401 && SIGNED_OUT_CODES.has(err.code) && !path.startsWith('/auth/login')) {
    useAuth.getState().clear(err.code === ERROR_CODES.SESSION_IDLE_TIMEOUT ? 'idle' : 'revoked');
  }
  throw new ApiError(res.status, err.code, err.message, err.details, err.requestId);
}

export const api = {
  get: async <T>(path: string, query?: Query, signal?: AbortSignal) => (await request<T>(path, { query, signal })).data,
  page: async <T>(path: string, query?: Query, signal?: AbortSignal) => {
    const r = await request<T[]>(path, { query, signal });
    return { items: r.data, meta: r.meta! };
  },
  post: async <T>(path: string, body?: unknown) => (await request<T>(path, { method: 'POST', body: body ?? {} })).data,
  patch: async <T>(path: string, body: unknown) => (await request<T>(path, { method: 'PATCH', body })).data,
  put: async <T>(path: string, body: unknown) => (await request<T>(path, { method: 'PUT', body })).data,
  delete: async <T>(path: string) => (await request<T>(path, { method: 'DELETE' })).data,
};

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong';
}

/**
 * Download a file (Excel, PDF…) from an authenticated endpoint and hand it to the browser.
 * Refreshes the access token once if it has expired.
 */
export async function downloadFile(path: string, query?: Query, fallbackName = 'download', retried = false): Promise<void> {
  const token = useAuth.getState().accessToken;
  const res = await fetch(url(path, query), { credentials: 'include', headers: { 'x-stoplord-client': 'web', ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
  if (!res.ok) {
    const json = await res.json().catch(() => null);
    const err = json?.error ?? { code: ERROR_CODES.INTERNAL, message: res.statusText || 'Download failed' };
    if (res.status === 401 && err.code === ERROR_CODES.TOKEN_EXPIRED && !retried && (await refreshSession())) return downloadFile(path, query, fallbackName, true);
    throw new ApiError(res.status, err.code, err.message, err.details, err.requestId);
  }
  const blob = await res.blob();
  const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? fallbackName;
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}
